import { useMemo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Schedule } from "../utils/amortization";
import { formatBRL, formatBRLCompact } from "../utils/format";

// Original project chart colors
const C1 = "#073550"; // chart-1 dark blue
const C2 = "#F28C0F"; // chart-2 orange
const C3 = "#F28C0F"; // chart-3 orange
const C4 = "#D8261F"; // chart-4 red
const BORDER = "#e5e7eb";
const MUTED = "#6b7280";

const axisProps = {
  stroke: MUTED,
  fontSize: 11,
  tickLine: false,
  axisLine: false,
} as const;

const tooltipStyle = {
  backgroundColor: "#ffffff",
  border: `1px solid ${BORDER}`,
  borderRadius: "0.75rem",
  fontSize: "12px",
  color: "#111827",
};

function ChartCard({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border border-border bg-card p-4 shadow-sm ${className ?? ""}`}>
      <div className="mb-3">
        <h4 className="text-xl font-semibold">{title}</h4>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {children as React.ReactElement}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

type PieDatum = { name: string; value: number; color: string };
type LabelProps = { cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; percent: number };
const RAD = Math.PI / 180;

function renderPieLabel(props: unknown) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props as LabelProps;
  if (!percent || percent < 0.03) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RAD);
  const y = cy + radius * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#ffffff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
}

function CompositionPie({ data, ...rest }: { data: PieDatum[] } & Record<string, unknown>) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  return (
    <PieChart {...rest}>
      <Pie
        data={data}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="45%"
        outerRadius="80%"
        innerRadius="55%"
        paddingAngle={1}
        stroke="#ffffff"
        strokeWidth={2}
        label={renderPieLabel}
        labelLine={false}
      >
        {data.map((d) => <Cell key={d.name} fill={d.color} />)}
      </Pie>
      <Tooltip
        contentStyle={tooltipStyle}
        formatter={(v: number) =>
          `${formatBRL(Number(v))} (${total > 0 ? ((Number(v) / total) * 100).toFixed(1) : "0"}%)`
        }
      />
      <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
    </PieChart>
  );
}

function pieFor(s: Schedule): PieDatum[] {
  return [
    { name: "Financiado", value: s.principal, color: C1 },
    { name: "Juros", value: s.totalInterest, color: C4 },
    { name: "Correção", value: s.totalCorrection, color: C3 },
  ].filter((d) => d.value > 0.005);
}

interface Props {
  baseline: Schedule;
  withExtra: Schedule | null;
}

export function SimulatorPieCharts({ baseline, withExtra }: Props) {
  const baselinePie = useMemo(() => pieFor(baseline), [baseline]);
  const withExtraPie = useMemo(() => (withExtra ? pieFor(withExtra) : null), [withExtra]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartCard
        title={withExtraPie ? "Sem amortização extra" : "Composição do total pago"}
        subtitle="Financiado, juros e correção"
        className={withExtraPie ? undefined : "lg:col-span-2"}
      >
        <CompositionPie data={baselinePie} />
      </ChartCard>
      {withExtraPie && (
        <ChartCard title="Com amortização extra" subtitle="Financiado, juros e correção">
          <CompositionPie data={withExtraPie} />
        </ChartCard>
      )}
    </div>
  );
}

function ScenarioComposition({ schedule, ...rest }: { schedule: Schedule } & Record<string, unknown>) {
  const data = schedule.rows.map((r) => ({ month: r.month, amortizacao: r.amortization, juros: r.interest }));
  return (
    <BarChart {...rest} data={data} margin={{ left: 4, right: 8, top: 4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="month" {...axisProps} minTickGap={28} />
      <YAxis {...axisProps} tickFormatter={(v) => formatBRLCompact(Number(v))} width={56} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatBRL(Number(v))} labelFormatter={(l) => `Parcela ${l}`} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="amortizacao" name="Amortização mensal" stackId="c" fill={C1} />
      <Bar dataKey="juros" name="Juros" stackId="c" fill={C4} />
    </BarChart>
  );
}

function ScenarioInstallment({ schedule, ...rest }: { schedule: Schedule } & Record<string, unknown>) {
  const data = schedule.rows.map((r) => ({ month: r.month, parcela: r.payment, amortizacao: r.amortization }));
  return (
    <LineChart {...rest} data={data} margin={{ left: 4, right: 8, top: 4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="month" {...axisProps} minTickGap={28} />
      <YAxis {...axisProps} tickFormatter={(v) => formatBRLCompact(Number(v))} width={56} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatBRL(Number(v))} labelFormatter={(l) => `Parcela ${l}`} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Line type="monotone" dataKey="parcela" name="Parcela" stroke={C1} strokeWidth={2.5} dot={false} />
      <Line type="monotone" dataKey="amortizacao" name="Amortização mensal" stroke={C4} strokeWidth={2.5} dot={false} />
    </LineChart>
  );
}

export function SimulatorScenarioCharts({ baseline, withExtra }: Props) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartCard title="Sem amortização extra" subtitle="Composição das parcelas" className={withExtra ? undefined : "lg:col-span-2"}>
        <ScenarioComposition schedule={baseline} />
      </ChartCard>
      {withExtra && (
        <ChartCard title="Com amortização extra" subtitle="Composição das parcelas">
          <ScenarioComposition schedule={withExtra} />
        </ChartCard>
      )}
      <ChartCard title="Sem amortização extra" subtitle="Parcela e amortização mensal" className={withExtra ? undefined : "lg:col-span-2"}>
        <ScenarioInstallment schedule={baseline} />
      </ChartCard>
      {withExtra && (
        <ChartCard title="Com amortização extra" subtitle="Parcela e amortização mensal">
          <ScenarioInstallment schedule={withExtra} />
        </ChartCard>
      )}
    </div>
  );
}

export function SimulatorCharts({ baseline, withExtra }: Props) {
  const balanceData = useMemo(() => {
    const max = Math.max(baseline.rows.length, withExtra?.rows.length ?? 0);
    const data: { month: number; sem: number | null; com: number | null }[] = [];
    for (let m = 0; m < max; m++) {
      data.push({
        month: m + 1,
        sem: baseline.rows[m]?.balance ?? null,
        com: withExtra ? (withExtra.rows[m]?.balance ?? 0) : null,
      });
    }
    return data;
  }, [baseline, withExtra]);

  const compositionData = useMemo(
    () => baseline.rows.map((r) => ({ month: r.month, juros: r.interest, amortizacao: r.amortization })),
    [baseline],
  );

  const interestData = useMemo(
    () =>
      withExtra
        ? [
            { name: "Sem amortização", juros: baseline.totalInterest },
            { name: "Com amortização", juros: withExtra.totalInterest },
          ]
        : [],
    [baseline, withExtra],
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartCard title="Saldo devedor" subtitle={withExtra ? "Com e sem amortização extra" : "Evolução ao longo do tempo"}>
        <LineChart data={balanceData} margin={{ left: 4, right: 8, top: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
          <XAxis dataKey="month" {...axisProps} minTickGap={40} />
          <YAxis {...axisProps} tickFormatter={(v) => formatBRLCompact(Number(v))} width={56} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatBRL(Number(v))} labelFormatter={(l) => `Parcela ${l}`} />
          {withExtra && <Legend wrapperStyle={{ fontSize: 12 }} />}
          <Line type="monotone" dataKey="sem" name="Sem amortização" stroke={C1} strokeWidth={2.5} dot={false} />
          {withExtra && <Line type="monotone" dataKey="com" name="Com amortização" stroke={C2} strokeWidth={2.5} dot={false} />}
        </LineChart>
      </ChartCard>

      <ChartCard title="Composição da parcela" subtitle="Juros x amortização ao longo do tempo">
        <AreaChart data={compositionData} margin={{ left: 4, right: 8, top: 4 }}>
          <defs>
            <linearGradient id="gJuros" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C4} stopOpacity={0.6} />
              <stop offset="95%" stopColor={C4} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="gAmort" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C1} stopOpacity={0.6} />
              <stop offset="95%" stopColor={C1} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
          <XAxis dataKey="month" {...axisProps} minTickGap={40} />
          <YAxis {...axisProps} tickFormatter={(v) => formatBRLCompact(Number(v))} width={56} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatBRL(Number(v))} labelFormatter={(l) => `Parcela ${l}`} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area type="monotone" dataKey="amortizacao" name="Amortização" stackId="1" stroke={C1} fill="url(#gAmort)" />
          <Area type="monotone" dataKey="juros" name="Juros" stackId="1" stroke={C4} fill="url(#gJuros)" />
        </AreaChart>
      </ChartCard>

      {withExtra && (
        <ChartCard title="Total de juros pagos" subtitle="Comparativo com e sem amortização extra" className="lg:col-span-2">
          <BarChart data={interestData} margin={{ left: 4, right: 8, top: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} tickFormatter={(v) => formatBRLCompact(Number(v))} width={56} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatBRL(Number(v))} />
            <Bar dataKey="juros" name="Total de juros" radius={[8, 8, 0, 0]} fill={C4} />
          </BarChart>
        </ChartCard>
      )}
    </div>
  );
}
